import { el, mount, unmount, setAttr } from 'redom';

import SimpleDialogNative from 'generics/components/simple-dialog/native';
import DialogHeader from 'generics/components/dialog-section-header/native';

import {
  findIndexByPropertyValue
} from 'utilities/collection';

import './dialog-with-header.scss';

class DialogsManager {

  constructor() {

    this.activeDialogsCount = 0;
    this.activeDialogs = [];
    this.liveComponents = {};

    this.dialogsMountPoint = null;
    this.liveDialogsMountPoint = null;

  }

  initializeDialogsMountPoint() {

    this.dialogsMountPoint = el('#dialogs-mount-point');

    mount(document.body, this.dialogsMountPoint);

    this.liveDialogsMountPoint = el('#live-dialogs-mount-point');

    mount(document.body, this.liveDialogsMountPoint);

  }

  initialize() {

    this.initializeDialogsMountPoint();

  }

  getActiveDialogsCount() {

    return this.activeDialogsCount;

  }

  incrementActiveDialogsCount() {

    this.activeDialogsCount++;

  }

  decrementActiveDialogsCount() {

    this.activeDialogsCount--;

  }

  // `dialog-is-on` on the body is what makes a dialog visible at all — without it every
  // `.simple-dialog-main` is `display: none`. The class is on the body, but it is not ours alone:
  // the components loaded from browser-components carry their own dialogs manager with their own
  // count, and the last of theirs to close takes the class off the body while ours are still up.
  // So the class is set from our count every time rather than only on the way to and from zero,
  // and a dialog of ours that outlives one of theirs gets it back.
  syncDialogStackClass() {

    if (this.activeDialogsCount === 0) {
      document.body.classList.remove('dialog-is-on');
      return;
    }

    document.body.classList.add('dialog-is-on');

  }

  dialogActivated() {

    this.incrementActiveDialogsCount();

    this.syncDialogStackClass();

  }

  dialogDeactivated() {

    this.decrementActiveDialogsCount();

    this.syncDialogStackClass();

  }

  unmountDialogById(dialogId) {

    const dialogIndex = findIndexByPropertyValue(this.activeDialogs, 'id', dialogId);

    if (dialogIndex === -1) {
      throw new Error(`${dialogId} does not present in active dialogs`);
    }

    const [dialogRecord] = this.activeDialogs.splice(dialogIndex, 1);

    unmount(this.dialogsMountPoint, dialogRecord.dialog);

    window.dialogsManager = this;

  }

  handleCloseCallback(dialogId, resolve) {

    this.unmountDialogById(dialogId);

    resolve({ cancelled: true });

  }

  handleCompleteCallback(dialogId, resolve, value) {

    this.unmountDialogById(dialogId);

    resolve({
      cancelled: false,
      value
    });

  }

  displayComponent(component, options = {}, mountPoint = this.dialogsMountPoint) {

    return new Promise(function resolver(resolve) {

      const dialogId = 'dialog-' + Date.now();

      const dialogConfiguration = {
        onClose: this.handleCloseCallback.bind(this, dialogId, resolve),
        onComplete: this.handleCompleteCallback.bind(this, dialogId, resolve)
      };

      const dialogContent = component;

      component.setDialogCallbacks(dialogConfiguration);

      const dialogOptions = {
        contentHolderClassName: options.contentHolderClassName || '',
        content: dialogContent,
        onClose: dialogConfiguration.onClose
      };
      const dialog = new SimpleDialogNative(dialogOptions);

      this.activeDialogs.push({
        id: dialogId,
        dialog
      });

      mount(mountPoint, dialog);

      setTimeout(() => options.onShow(), 0);

    }.bind(this));

  }

  handleLiveDialogCloseCallback(name) {

    const resolve = this.liveComponents[name].resolve;
    const dialog = this.liveComponents[name].dialog;

    dialog.hide();

    resolve({ cancelled: true });

  }

  handleLiveDialogCompleteCallback(name, value) {

    const resolve = this.liveComponents[name].resolve;
    const dialog = this.liveComponents[name].dialog;

    dialog.hide();

    resolve({
      cancelled: false,
      value
    });

  }

  mountLiveComponent(name, contentContainerId, component, options = {}) {

    const dialogId = 'dialog-' + Date.now();

    const dialogConfiguration = {
      onClose: this.handleLiveDialogCloseCallback.bind(this, name),
      onComplete: this.handleLiveDialogCompleteCallback.bind(this, name)
    };

    const dialogContent = component;

    component.setDialogCallbacks(dialogConfiguration);

    const dialogOptions = {
      contentContainerId,
      contentHolderClassName: options.contentHolderClassName || '',
      content: dialogContent,
      onClose: dialogConfiguration.onClose,
      startHidden: true
    };
    const dialog = new SimpleDialogNative(dialogOptions);

    this.activeDialogs.push({
      id: dialogId,
      dialog
    });

    mount(this.liveDialogsMountPoint, dialog);

    return dialog;

  }

  display(DialogContent, contentConfiguration = {}, options = {}) {

    return new Promise(function resolver(resolve, reject) {

      try {

        const dialogId = 'dialog-' + Date.now();

        const dialogConfiguration = {
          onClose: this.handleCloseCallback.bind(this, dialogId, resolve),
          onComplete: this.handleCompleteCallback.bind(this, dialogId, resolve)
        };

        const dialogContent = new DialogContent(contentConfiguration, dialogConfiguration);

        const dialogOptions = {
          contentHolderClassName: options.contentHolderClassName || '',
          containerClassName: options.containerClassName,
          content: dialogContent,
          onClose: dialogConfiguration.onClose,
          promptMode: options.promptMode
        };
        const dialog = new SimpleDialogNative(dialogOptions);

        this.activeDialogs.push({
          id: dialogId,
          dialog
        });

        mount(this.dialogsMountPoint, dialog);

        // A dialog displayed from here is closed by the user, and the promise above is how
        // the caller hears about it. A caller that also has to close it — because the URL it
        // is driven by changed under it — asks for a handle and gets one.
        if (typeof options.onDialogReady === 'function') {
          options.onDialogReady({ close: dialogConfiguration.onClose });
        }

      } catch (exception) {

        reject(exception);

      }

    }.bind(this));

  }

  displayWithHeader(DialogContent, options) {

    return new Promise(function resolver(resolve, reject) {

      try {

        const dialogId = 'dialog-' + Date.now();

        const dialogConfiguration = {
          onClose: this.handleCloseCallback.bind(this, dialogId, resolve),
          onComplete: this.handleCompleteCallback.bind(this, dialogId, resolve)
        };

        const header = new DialogHeader({ headerText: options.title, onClose: dialogConfiguration.onClose });
        const dialogContent = new DialogContent(options.contentConfiguration || {}, dialogConfiguration);

        const dialogOptions = {
          contentHolderClassName: options.contentHolderClassName || '',
          containerClassName: options.containerClassName,
          content: el('div.dialog-with-header', [header, dialogContent]),
          onClose: dialogConfiguration.onClose
        };
        const dialog = new SimpleDialogNative(dialogOptions);

        this.activeDialogs.push({
          id: dialogId,
          dialog
        });

        mount(this.dialogsMountPoint, dialog);

        // The handle `display` hands back, plus the content it was handed to. A caller whose URL
        // describes what is inside the dialog has to be able to close it and to tell it what the
        // URL now says, neither of which the promise above can do.
        if (typeof options.onDialogReady === 'function') {
          options.onDialogReady({
            close: dialogConfiguration.onClose,
            content: dialogContent
          });
        }

        if (typeof dialogContent.onShow === 'function') {
          dialogContent.onShow();
        }

      } catch (exception) {

        reject(exception);

      }

    }.bind(this));

  }

  showSpinnerDialog(onComplete) {

    const dialogId = 'dialog-' + Date.now();

    const dialogConfiguration = {
      onClose: this.handleCloseCallback.bind(this, dialogId, onComplete),
      onComplete: this.handleCompleteCallback.bind(this, dialogId, onComplete)
    };

    const dialogContent = el('ht-spinner');

    const dialogOptions = {
      contentHolderClassName: '',
      content: dialogContent,
      onClose: dialogConfiguration.onClose
    };
    const dialog = new SimpleDialogNative(dialogOptions);

    this.activeDialogs.push({
      id: dialogId,
      dialog
    });

    mount(this.dialogsMountPoint, dialog);

    const result = {
      hide() {
        dialogConfiguration.onClose();
      }
    };

    return result;

  }

  registerLiveComponent(name, component, options) {

    const contentContainerId = `${name}-dialog-main`;

    this.liveComponents[name] = { component, options, contentContainerId };

    const dialog = this.mountLiveComponent(name, contentContainerId, component, options);

    this.liveComponents[name].dialog = dialog;

  }

  showLiveComponent(name) {

    if (!this.liveComponents[name]) {
      throw new Error(`Live component missing: ${name}`);
    }

    return new Promise(function resolver(resolve) {

      const { dialog, options } = this.liveComponents[name];

      this.liveComponents[name].resolve = resolve;

      dialog.show();
      options.onShow();

    }.bind(this));

  }

  hideLiveComponent(name) {

    if (!this.liveComponents[name]) {
      throw new Error(`Live component missing: ${name}`);
    }

    const { dialog } = this.liveComponents[name];

    dialog.hide();

  }

}

export default DialogsManager;